// Port of ios/Sources/ViewModels/QuickReactions.swift (QuickReactions + ReactionTime).
import { clock as clockText } from './clockStyle';
import { EmojiCharacters } from './emojiCharacters';
import { civil, dateFormatter, isSameDay, localTimeZone } from './time';
import { locale as uiLocale } from '../i18n/i18n';

/**
 * The emoji a reader reaches for most, one click away — the webapp's hover "recent reactions"
 * (user, 2026-10-07: "add the most common reactions like on web as quick access"). Recents
 * first, then Android's `FALLBACK_REACTIONS`, deduped; custom emoji in the recents are skipped
 * because the row draws a text glyph. Twin of Android's `EmojiRecents` + `QuickReactionRow` rule.
 */

/**
 * Android's `FALLBACK_REACTIONS`: fills the row on a fresh install, where a suggestion is
 * most useful and least available. Names, not glyphs — the wire wants shortcodes.
 */
const fallback: readonly string[] = ['+1', 'heart', 'joy', 'open_mouth', 'cry', 'pray'];

/** The storage key the emoji picker's `remember` writes (Apple: `@AppStorage("emoji_recents")`). */
const recentsKey = 'emoji_recents';

function names(
  recentsRaw: string,
  limit: number,
  hasGlyph: (name: string) => boolean = (name) => EmojiCharacters.character(name) != null,
): string[] {
  // Swift's `split(separator: ",")` drops empty pieces.
  const recents = recentsRaw.split(',').filter((s) => s !== '');
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of [...recents, ...fallback]) {
    if (!hasGlyph(name) || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out.slice(0, Math.max(0, limit));
}

export const QuickReactions = {
  fallback,
  recentsKey,
  names,
} as const;

export interface ReactionTimeOptions {
  /** Epoch ms — the only "now". */
  now: number;
  /** IANA zone; undefined = the machine's. */
  zone?: string;
  /** BCP-47 tag; defaults to the UI language's. */
  locale?: string;
}

// WEB: Spanish month abbreviations come from this table, not from Intl. Apple formats with `es_ES`
// (`UiLocale.spanish`), whose CLDR abbreviation for September is "sept"; the web's Spanish locale
// is `es-MX`, whose Intl data says "sep", and older browser ICU builds add trailing dots. The
// table produces the Swift result in every browser (same choice as relativeTime.ts).
const SPANISH_MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * When a reaction happened, as the who-reacted surfaces print it: the bare clock for today
 * (the common case — a reaction to a message you are looking at), the day and clock otherwise,
 * in the same "d MMM · HH:mm" shape the seen sheet already uses. Zero means the server never
 * told us (rows written before `create_at` was stored) — printed as nothing, never as 1970.
 */
function label(epochMillis: number, options: ReactionTimeOptions): string | null {
  if (epochMillis <= 0) return null;
  const zone = options.zone ?? localTimeZone();
  const locale = options.locale ?? uiLocale();
  const c = civil(epochMillis, zone);
  const clock = clockText(c.hour, c.minute);
  if (isSameDay(epochMillis, options.now, zone)) return clock;
  // The Swift pattern is "d MMM · HH:mm" (es) / "MMM d · HH:mm" (en); assembled by hand so the
  // order and separators are exactly that.
  if (locale.toLowerCase().startsWith('es')) return `${c.day} ${SPANISH_MONTHS[c.month - 1]} · ${clock}`;
  const month = dateFormatter(locale, { month: 'short' }, zone).format(new Date(epochMillis));
  return `${month} ${c.day} · ${clock}`;
}

export const ReactionTime = {
  label,
} as const;
